import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { currentZoneFor, TimeRole, zoneLabel } from "./zone";

// _app extends these too; extending here keeps the module usable on its own (unit tests, workers).
dayjs.extend(utc);
dayjs.extend(timezone);

export type TimeInput = Dayjs | Date | number | string;

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Read a value that is a point in time. Epoch ms, a Date, a Dayjs, or an ISO string with `Z` or an
 * offset are unambiguous. A zone-less datetime string is not: the API contract (ted#2544) says it
 * is UTC, so it is read as UTC (not as device time, which is what `dayjs(string)` does) and flagged
 * in development so the endpoint can be fixed.
 */
export const parseInstant = (value: TimeInput): Dayjs => {
    if (dayjs.isDayjs(value)) return value.utc();
    if (typeof value === "string") {
        const s = value.trim();
        if (HAS_OFFSET.test(s) || DATE_ONLY.test(s)) return dayjs.utc(s);
        if (process.env.NODE_ENV === "development") console.warn(`[time] zone-less datetime "${s}" read as UTC (ted#2544)`);
        return dayjs.utc(s);
    }
    return dayjs.utc(value);
};

export interface FormatTimeOptions {
    /** What the value is; decides the zone. Default "instant". */
    role?: TimeRole;
    /** The instrument's `exchange_tz`, for the "market" and "trading" roles. */
    exchangeTz?: string | null;
    /** Append the zone abbreviation ("14:05 EDT"). */
    withZone?: boolean;
}

/** The one path from a time value to text. `format` is a dayjs format string. */
export const formatTime = (value: TimeInput | null | undefined, format: string, { role = "instant", exchangeTz, withZone = false }: FormatTimeOptions = {}): string => {
    if (value === null || value === undefined || value === "") return "";
    const instant = parseInstant(value);
    if (!instant.isValid()) return "";
    const zone = currentZoneFor(role, exchangeTz);
    const text = (zone === "UTC" ? instant : instant.tz(zone)).format(format);
    return withZone ? `${text} ${zoneLabel(zone, instant.valueOf())}` : text;
};

/** The value as a Dayjs in the zone its role resolves to, for code that needs more than format(). */
export const inDisplayZone = (value: TimeInput, role: TimeRole = "instant", exchangeTz?: string | null): Dayjs => {
    const instant = parseInstant(value);
    const zone = currentZoneFor(role, exchangeTz);
    return zone === "UTC" ? instant : instant.tz(zone);
};
